import React from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import {
  searchUsersForEventAdmin,
  useAddEventAdmin,
  useEventAdmins,
  useRemoveEventAdmin,
  type EventAdminUser,
} from '../../hooks/useEventAdmins';

type EventAdminsPanelProps = {
  eventId?: string;
};

const EventAdminsPanel: React.FC<EventAdminsPanelProps> = ({ eventId }) => {
  const { user } = useAuth();
  const { data: admins = [], isLoading, error } = useEventAdmins(eventId);
  const { mutateAsync: addAdmin, isPending: isAdding } = useAddEventAdmin(eventId);
  const { mutateAsync: removeAdmin, isPending: isRemoving } = useRemoveEventAdmin(eventId);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<EventAdminUser[]>([]);
  const [searchError, setSearchError] = React.useState<string | null>(null);
  const [isSearching, setIsSearching] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionNotice, setActionNotice] = React.useState<string | null>(null);
  const [busyUserId, setBusyUserId] = React.useState<string | null>(null);

  const assignedIds = React.useMemo(() => new Set(admins.map((row) => row.user_id)), [admins]);

  const handleSearch = async () => {
    setActionError(null);
    setActionNotice(null);
    const normalized = searchQuery.trim();
    if (normalized.length < 2) {
      setSearchError('Search at least 2 characters.');
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    setSearchError(null);
    try {
      const results = await searchUsersForEventAdmin(normalized);
      const filtered = results.filter((row) => !assignedIds.has(row.id));
      setSearchResults(filtered);
      if (!filtered.length) {
        setSearchError('No matching users available to add.');
      }
    } catch (searchErr) {
      setSearchResults([]);
      setSearchError(searchErr instanceof Error ? searchErr.message : 'Failed to search users.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleAdd = async (candidate: EventAdminUser) => {
    if (!eventId) return;
    setBusyUserId(candidate.id);
    setActionError(null);
    setActionNotice(null);
    try {
      await addAdmin({ userId: candidate.id, createdBy: user?.id ?? null });
      setSearchResults((previous) => previous.filter((row) => row.id !== candidate.id));
      setActionNotice(`${candidate.full_name || candidate.email || 'User'} can now manage this event's registrants.`);
    } catch (addErr) {
      setActionError(addErr instanceof Error ? addErr.message : 'Failed to add event admin.');
    } finally {
      setBusyUserId(null);
    }
  };

  const handleRemove = async (userId: string, label: string) => {
    if (!eventId) return;
    setBusyUserId(userId);
    setActionError(null);
    setActionNotice(null);
    try {
      await removeAdmin(userId);
      setActionNotice(`${label} is no longer an event admin for this event.`);
    } catch (removeErr) {
      setActionError(removeErr instanceof Error ? removeErr.message : 'Failed to remove event admin.');
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <Card>
      <Title>Event Admins</Title>
      <Hint>
        Event admins can manage this event's registrants, check-in, t-shirts, and email/SMS. They cannot
        edit event setup, tickets, or the registration form.
      </Hint>

      <SearchRow>
        <SearchInput
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search name or email"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void handleSearch();
            }
          }}
        />
        <ActionButton type="button" onClick={() => void handleSearch()} disabled={isSearching}>
          {isSearching ? 'Searching…' : 'Search'}
        </ActionButton>
      </SearchRow>
      {searchError ? <ErrorText>{searchError}</ErrorText> : null}

      {searchResults.length > 0 ? (
        <List>
          {searchResults.map((candidate) => (
            <ListRow key={candidate.id}>
              <Person>
                <PersonName>{candidate.full_name || 'Unnamed user'}</PersonName>
                <PersonMeta>{candidate.email || candidate.id}</PersonMeta>
              </Person>
              <ActionButton
                type="button"
                onClick={() => void handleAdd(candidate)}
                disabled={isAdding || busyUserId === candidate.id}
              >
                {busyUserId === candidate.id ? 'Adding…' : 'Make Event Admin'}
              </ActionButton>
            </ListRow>
          ))}
        </List>
      ) : null}

      {actionError ? <ErrorText>{actionError}</ErrorText> : null}
      {actionNotice ? <NoticeText>{actionNotice}</NoticeText> : null}
      {error ? <ErrorText>{error instanceof Error ? error.message : 'Could not load event admins.'}</ErrorText> : null}

      <SectionTitle>Assigned admins</SectionTitle>
      {isLoading ? <Hint>Loading…</Hint> : null}
      {!isLoading && admins.length === 0 ? <Hint>No event admins assigned yet.</Hint> : null}
      {admins.length > 0 ? (
        <List>
          {admins.map((row) => {
            const label = row.user?.full_name || row.user?.email || row.user_id;
            return (
              <ListRow key={row.user_id}>
                <Person>
                  <PersonName>{row.user?.full_name || 'Unnamed user'}</PersonName>
                  <PersonMeta>{row.user?.email || row.user_id}</PersonMeta>
                </Person>
                <DangerButton
                  type="button"
                  onClick={() => void handleRemove(row.user_id, label)}
                  disabled={isRemoving || busyUserId === row.user_id}
                >
                  {busyUserId === row.user_id ? 'Removing…' : 'Remove'}
                </DangerButton>
              </ListRow>
            );
          })}
        </List>
      ) : null}
    </Card>
  );
};

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 1.25rem;
`;

const Title = styled.h3`
  margin: 0 0 0.4rem;
  font-size: 1.05rem;
`;

const SectionTitle = styled.h4`
  margin: 1.25rem 0 0.5rem;
  font-size: 0.92rem;
`;

const Hint = styled.p`
  margin: 0 0 1rem;
  color: var(--onSurfaceVariant);
  font-size: 0.88rem;
  line-height: 1.45;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1 1 220px;
  min-height: 42px;
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.5rem 0.7rem;
  background: var(--surface);
  color: var(--onSurface);
`;

const ActionButton = styled.button`
  min-height: 42px;
  border: 1px solid var(--primaryDark);
  background: var(--primary);
  color: var(--onPrimary);
  border-radius: 8px;
  padding: 0.45rem 0.85rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  min-height: 42px;
  border: 1px solid var(--error);
  background: transparent;
  color: var(--error);
  border-radius: 8px;
  padding: 0.45rem 0.85rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const List = styled.div`
  display: grid;
  gap: 0.55rem;
  margin-top: 0.75rem;
`;

const ListRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.7rem 0.8rem;
`;

const Person = styled.div`
  min-width: 0;
`;

const PersonName = styled.div`
  font-weight: 700;
`;

const PersonMeta = styled.div`
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ErrorText = styled.p`
  color: var(--error);
  margin: 0.65rem 0 0;
  font-size: 0.88rem;
`;

const NoticeText = styled.p`
  color: var(--primary);
  margin: 0.65rem 0 0;
  font-size: 0.88rem;
`;

export default EventAdminsPanel;
